/**
 * ─── Site audio controller ────────────────────────────────────────────────────
 *
 * The single owner of the generative audio engine. Holds one lazily-created
 * GenerativeAudioEngine singleton and exposes flat, side-effect-safe functions
 * that React components (via BackgroundAudioProvider) can call at any time.
 *
 * Backend selection is driven by DEFAULT_AUDIO_MODE (audioModes.js):
 *   - GENERATIVE → these functions drive the Tone.js engine.
 *   - MP3 / OFF  → the generative functions are no-ops; BackgroundAudioProvider
 *                  keeps owning the legacy MP3 <audio> element (or silence).
 *
 * IMPORTANT — Tone.js is loaded with a dynamic import() inside ensureEngine(),
 * NOT a static import at module top. Tone eagerly creates an AudioContext the
 * moment its module evaluates (its index.js touches getContext() at top level),
 * which logs an autoplay warning if it happens before a user gesture. Deferring
 * the import to the first start() (a gesture) avoids that warning AND keeps Tone
 * out of the initial bundle. So section / hold / mute calls made before the user
 * has enabled audio are *buffered* here and replayed once the engine exists.
 * ──────────────────────────────────────────────────────────────────────────── */

import { AUDIO_MODES, DEFAULT_AUDIO_MODE, AUDIO_DEBUG } from './audioModes';

/* eslint-disable no-console */
function log(...args) {
    if (AUDIO_DEBUG) console.log('[audio]', ...args);
}
/* eslint-enable no-console */

let engine = null; // GenerativeAudioEngine instance — created on first start()
let enginePromise = null; // in-flight dynamic import + construct (dedupes concurrent starts)

// Latest values requested before the engine (and Tone) exist. Replayed in order
// the moment the engine is constructed so it starts at the right baseline.
let pendingSection = null;
let pendingHold = 0;
let pendingMuted = false;

function isGenerative() {
    return DEFAULT_AUDIO_MODE === AUDIO_MODES.GENERATIVE;
}

// Dynamically import Tone + the engine ONLY when first needed (a user gesture).
// This is what keeps the AudioContext from being created before interaction.
function ensureEngine() {
    if (engine) return Promise.resolve(engine);
    if (!enginePromise) {
        log('ensureEngine() — dynamic import of GenerativeAudioEngine (loads Tone chunk)…');
        enginePromise = import('./GenerativeAudioEngine').then(({ default: GenerativeAudioEngine }) => {
            log('engine chunk imported — constructing engine');
            engine = new GenerativeAudioEngine();
            // Replay anything buffered before the engine existed.
            if (pendingSection) engine.setSection(pendingSection);
            if (pendingHold) engine.setHoldIntensity(pendingHold);
            if (pendingMuted) engine.setMuted(true);
            log('engine constructed — pending section:', pendingSection, '| hold:', pendingHold, '| muted:', pendingMuted);
            return engine;
        });
    }
    return enginePromise;
}

export function getSiteAudioMode() {
    return DEFAULT_AUDIO_MODE;
}

/**
 * Start (or resume) generative audio. Must be called from a user gesture: the
 * dynamic import + the engine's Tone.start() both run inside the gesture, so the
 * AudioContext is created and resumed only after interaction. Returns true if
 * generative audio was started, false in MP3/OFF mode (caller owns playback).
 */
export async function startSiteAudio() {
    log('startSiteAudio() called — mode =', DEFAULT_AUDIO_MODE);
    if (!isGenerative()) return false;
    const e = await ensureEngine();
    await e.start();
    log('startSiteAudio() resolved — engine.isStarted() =', e.isStarted());
    return true;
}

export function stopSiteAudio() {
    if (!isGenerative() || !engine) return;
    engine.stop();
}

export function setSiteAudioMuted(isMuted) {
    if (!isGenerative()) return;
    if (engine) engine.setMuted(isMuted);
    else pendingMuted = !!isMuted; // buffer — do NOT import Tone before the gesture
}

export function setSiteAudioSection(sectionName) {
    if (!isGenerative()) return;
    if (engine) engine.setSection(sectionName);
    else pendingSection = sectionName; // buffer — do NOT import Tone before the gesture
}

export function setSiteAudioHoldIntensity(value) {
    if (!isGenerative()) return;
    if (engine) engine.setHoldIntensity(value);
    else pendingHold = value; // buffer — do NOT import Tone before the gesture
}

// ── Interaction events ──────────────────────────────────────────────────────────
// Transient perturbations of the bed. No buffering: before the engine exists there
// is no audio, and the event streams (hover/hold) are continuous + self-recovering,
// so a few dropped events during the first-gesture import are inaudible.
export function siteAudioHover() {
    if (engine) engine.hover();
}

export function siteAudioSectionTransition(fromName, toName) {
    if (engine) engine.sectionTransition(fromName, toName);
}

// Micro-sonification — committed navigation events (see lib/interactionAudio.js).
export function siteAudioProjectPreviewChange(slug) {
    if (engine) engine.projectPreviewChange(slug);
}

export function siteAudioProjectCanvasTransition(fromSlug, toSlug) {
    if (engine) engine.projectCanvasTransition(fromSlug, toSlug);
}

export function siteAudioProjectOpen(slug) {
    if (engine) engine.projectOpen(slug);
}

export function siteAudioProjectClose(slug) {
    if (engine) engine.projectClose(slug);
}

export function siteAudioLabNavigate(direction) {
    if (engine) engine.labNavigate(direction);
}

export function siteAudioHoldStart() {
    if (engine) engine.holdStart();
}

export function siteAudioHoldProgress(progress) {
    if (engine) engine.holdProgress(progress);
}

export function siteAudioHoldRelease() {
    if (engine) engine.holdRelease();
}

export function siteAudioHoldComplete() {
    if (engine) engine.holdComplete();
}

// Persistent sphere-open / manifesto state (ramps the sustained open ambience in/out).
// holdComplete() already enters the open state and the manifesto close (holdRelease)
// exits it, so these are exposed mainly for direct control + console auditioning.
export function siteAudioManifestoOpen() {
    if (engine) engine.manifestoOpen();
}

export function siteAudioManifestoClose() {
    if (engine) engine.manifestoClose();
}

export function isSiteAudioStarted() {
    return !!(engine && engine.isStarted());
}

export function isSiteAudioMuted() {
    return engine ? engine.isMuted() : pendingMuted;
}

export function disposeSiteAudio() {
    if (!engine) return;
    engine.dispose();
    engine = null;
    enginePromise = null;
}

// ── Development-only console debug API ──────────────────────────────────────────
// Lets sound design be tuned from the browser console without any production UI:
//   window.__KessonAudio.start()
//   window.__KessonAudio.section('lab')
//   window.__KessonAudio.hold(1); window.__KessonAudio.hold(0)
// Audition the sphere opening WITHOUT doing the whole hold ritual (start audio first):
//   window.__KessonAudio.testOpenBurst()        // just the non-tonal opening peak burst
//   window.__KessonAudio.testSphereOpenField() // ramp the persistent field in (…Off to fade)
//   window.__KessonAudio.testHoldComplete()    // the full opening (vacuum dip → release → field)
//   window.__KessonAudio.testReset()           // reset state so testHoldComplete can re-fire
// CRA inlines NODE_ENV, so this whole block is dead-code-eliminated in production.
if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
    window.__KessonAudio = {
        start: startSiteAudio,
        stop: stopSiteAudio,
        mute: () => setSiteAudioMuted(true),
        unmute: () => setSiteAudioMuted(false),
        section: setSiteAudioSection,
        hold: setSiteAudioHoldIntensity,
        // Interaction events — audition the reactivity from the console.
        hover: siteAudioHover,
        transition: siteAudioSectionTransition,
        projectPreview: siteAudioProjectPreviewChange,
        canvasTransition: siteAudioProjectCanvasTransition,
        projectOpen: siteAudioProjectOpen,
        projectClose: siteAudioProjectClose,
        labNav: siteAudioLabNavigate,
        // Interaction diagnostics: engine-side called/played counters. Compare with
        // window.__KessonInteractionPublished (UI-side publish counts) to localize
        // where a "silent" event was dropped (provider RM gate vs engine guards).
        counts: () => (engine ? engine._interactionDebug : null),
        holdStart: siteAudioHoldStart,
        holdProgress: siteAudioHoldProgress,
        holdRelease: siteAudioHoldRelease,
        holdComplete: siteAudioHoldComplete,
        manifestoOpen: siteAudioManifestoOpen,
        manifestoClose: siteAudioManifestoClose,
        // Temporary audition helpers (no-ops until audio is started).
        testOpenBurst: () => { if (engine && engine._triggerOpenBurst) engine._triggerOpenBurst(engine.nodes.masterGain.context.now()); },
        testSphereOpenField: () => { if (engine) engine.manifestoOpen(); },
        testSphereOpenFieldOff: () => { if (engine) engine.manifestoClose(); },
        testHoldComplete: () => { if (engine) engine.holdComplete(); },
        testReset: () => { if (engine) engine.holdRelease(); },
        dispose: disposeSiteAudio,
        mode: getSiteAudioMode,
    };
}
